/**
 * Embed boot parameters — parsed from the URL FRAGMENT of /embed.
 *
 * Everything (including the token) travels in the fragment so it is never sent
 * over the network as part of the request path and never appears in server
 * logs. Example:
 *   /embed#token=eyJ...&view=panorama&panoramaId=pano_204&locale=he
 *
 * Contract: docs/SPECIFICATIONS.md "External Embed Integration" §Frontend.
 */

export interface EmbedParams {
  token: string;
  view?: '3d' | 'panorama';
  panoramaId?: string;
  elementId?: number;
  storey?: string;
  locale?: 'he' | 'en';
  theme?: 'light' | 'dark';
}

export interface EmbedTokenClaims {
  type: 'embed';
  partnerId: string;
  siteId: string;
  scope: string[];
  endUserRef?: string;
  allowedOrigins?: string[];
  exp: number; // seconds since epoch (JWT standard)
}

export function isEmbedPath(pathname: string): boolean {
  return pathname === '/embed' || pathname === '/embed/';
}

/** Parse the /embed fragment. Returns null when no token is present. */
export function parseEmbedParams(hash: string): EmbedParams | null {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.substring(1) : hash);
  const token = params.get('token');
  if (!token) return null;

  const view = params.get('view');
  const locale = params.get('locale');
  const theme = params.get('theme');
  const elementIdRaw = params.get('elementId');
  const elementId = elementIdRaw !== null && /^\d+$/.test(elementIdRaw) ? Number(elementIdRaw) : undefined;

  return {
    token,
    view: view === '3d' || view === 'panorama' ? view : undefined,
    panoramaId: params.get('panoramaId') || undefined,
    elementId,
    storey: params.get('storey') || undefined,
    locale: locale === 'he' || locale === 'en' ? locale : undefined,
    theme: theme === 'light' || theme === 'dark' ? theme : undefined,
  };
}

/**
 * Client-side (unverified) decode of the embed JWT payload — used only for
 * allowedOrigins (postMessage targets) and exp (renewal timer). The server
 * verifies the signature on every API call; nothing security-critical hangs
 * off this decode alone.
 */
export function decodeEmbedTokenClaims(token: string): EmbedTokenClaims | null {
  try {
    const payloadPart = token.split('.')[1];
    const json = atob(payloadPart.replace(/-/g, '+').replace(/_/g, '/'));
    const claims = JSON.parse(json);
    return claims && claims.type === 'embed' ? (claims as EmbedTokenClaims) : null;
  } catch {
    return null;
  }
}
